import React, { useState, useMemo } from 'react';
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  HashtagIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { Button, Select } from '../../../common';
import SearchBar from '../../../common/SearchBar';
import { useAdminTranslation } from '../../../../modules/i18n';
import { KGImportValidationResult, KGDocumentMapping } from '../../../../types/knowledgeGraph';
import { safeRenderData } from '../../../../utils/safeRender';

interface KGValidationTableProps {
  validationResult: KGImportValidationResult;
  className?: string;
}

// Enhanced mapping status info for better display
interface EnhancedMapping extends KGDocumentMapping {
  internalMd5?: string | null;
  statusIcon?: React.ReactNode;
  statusColor?: string;
  statusMessage?: string;
  isPerfectMatch: boolean;
  matchDetails?: {
    md5Matched: boolean;
    hasInternalMd5: boolean;
  };
}

export const KGValidationTable: React.FC<KGValidationTableProps> = ({ validationResult, className = '' }) => {
  const { t } = useAdminTranslation();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'perfect' | 'partial' | 'missing' | 'new'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetails, setShowDetails] = useState(true); // Start expanded

  // Prepare table data with useMemo to avoid hooks in closures
  const tableData = useMemo(() => {
    if (!validationResult?.import_strategy) {
      return {
        allMappings: [],
        filtered: [],
        pageItems: [],
        totalPages: 0,
        currentPage: 1,
        start: 0,
        total: 0
      };
    }

    const strategy = validationResult.import_strategy as any;

    // Create enhanced mappings with additional display info
    const createEnhancedMapping = (mapping: KGDocumentMapping): EnhancedMapping => {
      // Extract match information from backend data (MD5-based only)
      const md5Matched = mapping.md5_matched ?? false;
      const hasInternalMd5 = Boolean(mapping.internal_md5);

      // Extract internal MD5 from conflict reason if not provided directly
      let internalMd5 = mapping.internal_md5;
      if (!internalMd5 && mapping.conflict_reason) {
        const md5Match = mapping.conflict_reason.match(/internal=([a-fA-F0-9]+)/);
        if (md5Match) {
          internalMd5 = md5Match[1];
        }
      }

      // Determine if this is a perfect match (MD5 matches and no conflicts)
      const isPerfectMatch = mapping.mapping_status === 'found' && md5Matched && !mapping.conflict_reason;

      const enhanced: EnhancedMapping = {
        ...mapping,
        internalMd5,
        statusIcon: null,
        statusColor: '',
        statusMessage: '',
        isPerfectMatch,
        matchDetails: {
          md5Matched,
          hasInternalMd5
        }
      };

      // Determine match details and styling
      if (mapping.mapping_status === 'found') {
        enhanced.statusIcon = <CheckCircleIcon className='h-4 w-4' />;
        enhanced.statusColor = 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      } else if (mapping.mapping_status === 'mismatch') {
        enhanced.statusIcon = <ExclamationTriangleIcon className='h-4 w-4' />;
        enhanced.statusColor = 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
      } else if (mapping.mapping_status === 'missing') {
        enhanced.statusIcon = <XMarkIcon className='h-4 w-4' />;
        enhanced.statusColor = 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
      } else if (mapping.mapping_status === 'new') {
        enhanced.statusIcon = <InformationCircleIcon className='h-4 w-4' />;
        enhanced.statusColor = 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      } else {
        enhanced.statusIcon = <QuestionMarkCircleIcon className='h-4 w-4' />;
        enhanced.statusColor = 'text-gray-600 bg-gray-100 dark:text-dark-text-secondary dark:bg-dark-background-tertiary';
      }

      return enhanced;
    };

    const allMappings: EnhancedMapping[] = [
      ...(strategy.documents_missing || []).map(createEnhancedMapping),
      ...(strategy.documents_to_update || []).map(createEnhancedMapping),
      ...(strategy.documents_found || []).map(createEnhancedMapping),
      ...(strategy.documents_conflicted || []).map(createEnhancedMapping)
    ];

    const filtered = allMappings.filter((m) => {
      const matchesSearch =
        !searchTerm ||
        (m.external_path || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.internal_path || '').toLowerCase().includes(searchTerm.toLowerCase());

      let matchesStatus = false;
      if (statusFilter === 'all') {
        matchesStatus = true;
      } else if (statusFilter === 'perfect') {
        matchesStatus = m.isPerfectMatch;
      } else if (statusFilter === 'partial') {
        matchesStatus = m.mapping_status === 'mismatch' || (m.mapping_status === 'found' && !m.isPerfectMatch);
      } else if (statusFilter === 'missing') {
        matchesStatus = m.mapping_status === 'missing';
      } else if (statusFilter === 'new') {
        matchesStatus = m.mapping_status === 'new';
      }

      return matchesSearch && matchesStatus;
    });

    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);
    const start = (currentPage - 1) * pageSize;
    const pageItems = filtered.slice(start, start + pageSize);

    return {
      allMappings,
      filtered,
      pageItems,
      totalPages,
      currentPage,
      start,
      total
    };
  }, [validationResult, searchTerm, statusFilter, page, pageSize]);

  // Define table columns with enhanced MD5 display
  const columns = useMemo<ColumnDef<EnhancedMapping>[]>(
    () => [
      {
        header: t('content.knowledgeGraph.importManager.table.externalDoc'),
        accessorKey: 'external_path',
        size: 280,
        cell: ({ row }) => {
          const mapping = row.original;
          const fileName = mapping.external_path.split('/').pop() || '';
          const pathWithoutFile = mapping.external_path.replace('/' + fileName, '');
          return (
            <div className='max-w-xs'>
              <div className='truncate font-medium text-gray-900 dark:text-dark-text' title={fileName}>
                {fileName}
              </div>
              <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary' title={pathWithoutFile}>
                {pathWithoutFile}
              </div>
              <div className='mt-1 flex items-center text-xs text-gray-400 dark:text-dark-text-secondary'>
                <HashtagIcon className='mr-1 h-3 w-3' />
                <span className='truncate font-mono' title={mapping.external_md5}>
                  {mapping.external_md5?.slice(0, 8)}...
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: t('content.knowledgeGraph.importManager.table.externalMD5'),
        accessorKey: 'external_md5',
        size: 120,
        cell: ({ row }) => {
          const mapping = row.original;
          return (
            <div className='font-mono text-xs'>
              <div className='truncate text-gray-700 dark:text-dark-text-secondary' title={mapping.external_md5}>
                {mapping.external_md5?.slice(0, 12)}...
              </div>
              <div className='mt-1 text-[10px] text-gray-500 dark:text-dark-text-secondary'>External</div>
            </div>
          );
        }
      },
      {
        header: t('content.knowledgeGraph.importManager.table.internalMD5'),
        accessorKey: 'internal_md5',
        size: 120,
        cell: ({ row }) => {
          const mapping = row.original;
          const hasInternalMd5 = Boolean(mapping.internalMd5);
          const md5sMatch = hasInternalMd5 && mapping.external_md5 && mapping.external_md5.toLowerCase() === mapping.internalMd5?.toLowerCase();

          if (!mapping.internalMd5) {
            return (
              <div className='text-xs italic text-gray-400 dark:text-dark-text-secondary'>
                {mapping.mapping_status === 'missing' ? t('content.knowledgeGraph.importManager.table.notFound') : '-'}
              </div>
            );
          }

          return (
            <div className='font-mono text-xs'>
              <div
                className={`truncate ${md5sMatch ? 'font-medium text-green-700 dark:text-green-400' : 'font-medium text-red-600 dark:text-red-400'}`}
                title={mapping.internalMd5}
              >
                {mapping.internalMd5?.slice(0, 12)}...
              </div>
              <div className='mt-1 flex items-center text-[10px]'>
                <span className='text-gray-500 dark:text-dark-text-secondary'>Internal</span>
                {md5sMatch ? (
                  <CheckCircleIcon className='ml-1 h-3 w-3 text-green-500 dark:text-green-400' />
                ) : (
                  <XMarkIcon className='ml-1 h-3 w-3 text-red-500 dark:text-red-400' />
                )}
              </div>
            </div>
          );
        }
      },
      {
        header: t('content.knowledgeGraph.importManager.table.matchStatus'),
        accessorKey: 'mapping_status',
        size: 160,
        cell: ({ row }) => {
          const mapping = row.original;
          const details = mapping.matchDetails;

          return (
            <div className='flex flex-col items-start space-y-2'>
              {/* Main status */}
              <div className={`flex items-center rounded-full px-2 py-1 text-xs font-semibold ${mapping.statusColor}`}>
                {mapping.statusIcon}
                <span className='ml-1 capitalize'>{mapping.mapping_status}</span>
              </div>

              {/* MD5 match details for non-missing documents */}
              {mapping.mapping_status !== 'missing' && details && (
                <div className='flex items-center space-x-1 text-xs'>
                  {details.md5Matched ? (
                    <CheckCircleIcon className='h-3 w-3 text-green-500 dark:text-green-400' />
                  ) : details.hasInternalMd5 ? (
                    <XMarkIcon className='h-3 w-3 text-red-500 dark:text-red-400' />
                  ) : (
                    <QuestionMarkCircleIcon className='h-3 w-3 text-gray-400 dark:text-dark-text-secondary' />
                  )}
                  <HashtagIcon className='h-3 w-3 text-gray-400 dark:text-dark-text-secondary' />
                  <span
                    className={`font-medium ${details.md5Matched ? 'text-green-600 dark:text-green-400' : details.hasInternalMd5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-dark-text-secondary'}`}
                  >
                    MD5 Match
                  </span>
                </div>
              )}
            </div>
          );
        }
      },
      {
        header: t('content.knowledgeGraph.importManager.table.internalDoc'),
        accessorKey: 'internal_path',
        size: 260,
        cell: ({ row }) => {
          const mapping = row.original;
          if (!mapping.internal_path && !mapping.internal_id) {
            return <span className='italic text-gray-400 dark:text-dark-text-secondary'>{t('content.knowledgeGraph.importManager.table.notFound')}</span>;
          }
          const fileName = mapping.internal_path?.split('/').pop() || '';
          const pathWithoutFile = mapping.internal_path?.replace('/' + fileName, '') || '';
          return (
            <div className='max-w-xs'>
              <div className='truncate font-medium text-gray-900 dark:text-dark-text' title={fileName}>
                {fileName}
              </div>
              {pathWithoutFile && (
                <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary' title={pathWithoutFile}>
                  {pathWithoutFile}
                </div>
              )}
              {mapping.internal_id && <div className='text-xs text-gray-400 dark:text-dark-text-secondary'>ID: {mapping.internal_id}</div>}
            </div>
          );
        }
      },
      {
        header: t('content.knowledgeGraph.importManager.validation.details'),
        accessorKey: 'conflict_reason',
        size: 340,
        cell: ({ row }) => {
          const mapping = row.original;
          const isConflict = mapping.mapping_status === 'mismatch';
          const isFound = mapping.mapping_status === 'found';
          const isMissing = mapping.mapping_status === 'missing';
          const isNew = mapping.mapping_status === 'new';

          // Show success message for perfect matches
          if (isFound && !mapping.conflict_reason) {
            return (
              <div className='flex items-center text-sm text-green-600 dark:text-green-400'>
                <CheckCircleIcon className='mr-2 h-4 w-4' />
                <div>
                  <div className='font-medium'>{t('content.knowledgeGraph.importManager.table.perfectMatch')}</div>
                  <div className='mt-1 text-xs text-green-500 dark:text-green-300'>
                    {t('content.knowledgeGraph.importManager.table.perfectMatchDesc')}
                  </div>
                </div>
              </div>
            );
          }

          // Show conflict details
          if (mapping.conflict_reason) {
            return (
              <div
                className={`rounded-lg border p-2 text-sm ${
                  isConflict
                    ? 'border-orange-200 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                    : isMissing
                      ? 'border-red-200 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                      : 'border-yellow-200 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/20'
                }`}
              >
                <div className='mb-2 text-gray-800 dark:text-dark-text'>{mapping.conflict_reason}</div>

                {/* MD5 comparison for mismatches */}
                {isConflict && (
                  <div className='space-y-1 text-xs'>
                    <div className='grid grid-cols-2 gap-2'>
                      <div className='flex flex-col'>
                        <span className='font-medium text-gray-600 dark:text-dark-text-secondary'>External MD5:</span>
                        <span className='mt-1 font-mono text-orange-600 dark:text-orange-400'>{mapping.external_md5?.slice(0, 16)}...</span>
                      </div>
                      {mapping.internalMd5 && (
                        <div className='flex flex-col'>
                          <span className='font-medium text-gray-600 dark:text-dark-text-secondary'>Internal MD5:</span>
                          <span className='mt-1 font-mono text-blue-600 dark:text-blue-400'>{mapping.internalMd5.slice(0, 16)}...</span>
                        </div>
                      )}
                    </div>
                    <div className='mt-2 flex items-center font-medium text-orange-700 dark:text-orange-300'>
                      <ExclamationTriangleIcon className='mr-1 h-3 w-3' />
                      {t('content.knowledgeGraph.importManager.table.contentChanged')}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Default cases
          if (isNew) {
            return (
              <div className='flex items-center text-sm text-blue-600 dark:text-blue-400'>
                <InformationCircleIcon className='mr-2 h-4 w-4' />
                <span>{t('content.knowledgeGraph.importManager.table.newDocument')}</span>
              </div>
            );
          }

          return <span className='text-gray-400 dark:text-dark-text-secondary'>-</span>;
        }
      }
    ],
    [t]
  );

  // Create table instance at top level
  const table = useReactTable({
    data: tableData.pageItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange'
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary Stats */}
      <div className='grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-dark-text-secondary sm:grid-cols-5'>
        <div className='flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-2 py-1 dark:border-border-dark dark:bg-dark-background-tertiary'>
          <span className='flex items-center'>
            <DocumentTextIcon className='mr-1 h-3 w-3' />
            {t('content.knowledgeGraph.importManager.validation.totalDocs')}
          </span>
          <span className='ml-2 rounded bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'>
            {safeRenderData(validationResult.import_strategy?.total_external_documents)}
          </span>
        </div>
        <div className='flex items-center justify-between rounded border border-green-200 bg-green-50 px-2 py-1 dark:border-green-600 dark:bg-green-900/20'>
          <span className='flex items-center'>
            <CheckCircleIcon className='mr-1 h-3 w-3 text-green-600 dark:text-green-400' />
            {t('content.knowledgeGraph.importManager.validation.found')}
          </span>
          <span className='ml-2 rounded bg-green-200 px-2 py-0.5 text-[11px] font-semibold text-green-900 dark:bg-green-700 dark:text-green-200'>
            {safeRenderData(validationResult.import_strategy?.documents_found?.length || 0)}
          </span>
        </div>
        <div className='flex items-center justify-between rounded border border-orange-200 bg-orange-50 px-2 py-1 dark:border-orange-600 dark:bg-orange-900/20'>
          <span className='flex items-center'>
            <ExclamationTriangleIcon className='mr-1 h-3 w-3 text-orange-600 dark:text-orange-400' />
            {t('content.knowledgeGraph.importManager.validation.needUpdate')}
          </span>
          <span className='ml-2 rounded bg-orange-200 px-2 py-0.5 text-[11px] font-semibold text-orange-900 dark:bg-orange-700 dark:text-orange-200'>
            {safeRenderData(validationResult.import_strategy?.documents_to_update?.length || 0)}
          </span>
        </div>
        <div className='flex items-center justify-between rounded border border-red-200 bg-red-50 px-2 py-1 dark:border-red-600 dark:bg-red-900/20'>
          <span className='flex items-center'>
            <XMarkIcon className='mr-1 h-3 w-3 text-red-600 dark:text-red-400' />
            {t('content.knowledgeGraph.importManager.validation.missing')}
          </span>
          <span className='ml-2 rounded bg-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-900 dark:bg-red-700 dark:text-red-200'>
            {safeRenderData(validationResult.import_strategy?.documents_missing?.length || 0)}
          </span>
        </div>
        <div className='flex items-center justify-between rounded border border-blue-200 bg-blue-50 px-2 py-1 dark:border-blue-600 dark:bg-blue-900/20'>
          <span className='flex items-center'>
            <InformationCircleIcon className='mr-1 h-3 w-3 text-blue-600 dark:text-blue-400' />
            {t('content.knowledgeGraph.importManager.validation.conflicts')}
          </span>
          <span className='ml-2 rounded bg-blue-200 px-2 py-0.5 text-[11px] font-semibold text-blue-900 dark:bg-blue-700 dark:text-blue-200'>
            {safeRenderData(validationResult.import_strategy?.documents_conflicted?.length || 0)}
          </span>
        </div>
      </div>

      {/* Status Legend */}
      <div className='rounded-lg border bg-gray-50 p-3 dark:border-border-dark dark:bg-dark-background-tertiary'>
        <h5 className='mb-2 flex items-center text-xs font-semibold text-gray-800 dark:text-dark-text'>
          <QuestionMarkCircleIcon className='mr-1 h-3 w-3' />
          {t('content.knowledgeGraph.importManager.validation.statusExplanation')}
        </h5>
        <div className='grid grid-cols-1 gap-2 text-xs sm:grid-cols-2'>
          <div className='flex items-center space-x-2'>
            <CheckCircleIcon className='h-3 w-3 flex-shrink-0 text-green-600 dark:text-green-400' />
            <span className='text-gray-700 dark:text-dark-text-secondary'>
              <strong>{t('content.knowledgeGraph.importManager.validation.foundLabel')}</strong>{' '}
              {t('content.knowledgeGraph.importManager.validation.foundDesc')}
            </span>
          </div>
          <div className='flex items-center space-x-2'>
            <ExclamationTriangleIcon className='h-3 w-3 flex-shrink-0 text-orange-600 dark:text-orange-400' />
            <span className='text-gray-700 dark:text-dark-text-secondary'>
              <strong>{t('content.knowledgeGraph.importManager.validation.mismatchLabel')}</strong>{' '}
              {t('content.knowledgeGraph.importManager.validation.mismatchDesc')}
            </span>
          </div>
          <div className='flex items-center space-x-2'>
            <XMarkIcon className='h-3 w-3 flex-shrink-0 text-red-600 dark:text-red-400' />
            <span className='text-gray-700 dark:text-dark-text-secondary'>
              <strong>{t('content.knowledgeGraph.importManager.validation.missingLabel')}</strong>{' '}
              {t('content.knowledgeGraph.importManager.validation.missingDesc')}
            </span>
          </div>
          <div className='flex items-center space-x-2'>
            <InformationCircleIcon className='h-3 w-3 flex-shrink-0 text-blue-600 dark:text-blue-400' />
            <span className='text-gray-700 dark:text-dark-text-secondary'>
              <strong>{t('content.knowledgeGraph.importManager.validation.conflictedLabel')}</strong>{' '}
              {t('content.knowledgeGraph.importManager.validation.conflictedDesc')}
            </span>
          </div>
        </div>
      </div>

      {/* Collapsible Details Section */}
      <div className='rounded-md bg-white p-3 dark:bg-dark-background-tertiary'>
        <button type='button' onClick={() => setShowDetails(!showDetails)} className='flex w-full items-center justify-between'>
          <h4 className='text-sm font-medium text-gray-900 dark:text-dark-text'>{t('content.knowledgeGraph.importManager.validation.importInfo')}</h4>
          {showDetails ? (
            <ChevronDownIcon className='h-4 w-4 text-gray-500 dark:text-dark-text-secondary' />
          ) : (
            <ChevronRightIcon className='h-4 w-4 text-gray-500 dark:text-dark-text-secondary' />
          )}
        </button>

        {showDetails && (
          <div className='mt-3 space-y-3'>
            {/* Controls */}
            <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
              <div className='flex items-center gap-2'>
                <SearchBar
                  value={searchTerm}
                  onChange={(value) => {
                    setSearchTerm(value);
                    setPage(1);
                  }}
                  placeholder={t('content.knowledgeGraph.importManager.validation.searchPlaceholder')}
                  className='w-64'
                />
                <Select
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value as 'all' | 'perfect' | 'partial' | 'missing' | 'new');
                    setPage(1);
                  }}
                  options={[
                    { value: 'all', label: t('content.knowledgeGraph.importManager.validation.allStatuses') },
                    { value: 'perfect', label: t('content.knowledgeGraph.importManager.validation.perfectMatch') },
                    { value: 'partial', label: t('content.knowledgeGraph.importManager.validation.partialMatch') },
                    { value: 'missing', label: t('content.knowledgeGraph.importManager.validation.missingDoc') },
                    { value: 'new', label: t('content.knowledgeGraph.importManager.validation.newDoc') }
                  ]}
                  className='w-64'
                />
              </div>
              <div className='flex items-center gap-2 text-sm'>
                <span>{t('content.knowledgeGraph.importManager.validation.show')}</span>
                <Select
                  value={pageSize.toString()}
                  onChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                  options={[
                    { value: '10', label: '10' },
                    { value: '20', label: '20' },
                    { value: '50', label: '50' }
                  ]}
                  className='w-20'
                />
                <span>{t('content.knowledgeGraph.importManager.validation.items')}</span>
              </div>
            </div>

            {/* Desktop Table */}
            <div className='hidden md:block'>
              <div className='h-96 overflow-auto rounded border border-gray-200 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:border-border-dark dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800'>
                <table className='min-w-full text-sm'>
                  <thead className='sticky top-0 z-10 bg-gray-50 text-gray-700 shadow dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => (
                          <th key={h.id} style={{ width: h.getSize() }} className='relative px-3 py-2 text-left'>
                            {flexRender(h.column.columnDef.header, h.getContext())}
                            <div
                              onMouseDown={h.getResizeHandler()}
                              className='absolute right-0 top-0 h-full w-2 cursor-col-resize select-none bg-transparent hover:bg-gray-300'
                            />
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className='bg-white dark:bg-dark-background-secondary'>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className='border-t dark:border-border-dark'>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className='px-3 py-2 text-gray-700 dark:text-dark-text-secondary'>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {tableData.pageItems.length === 0 && (
                      <tr>
                        <td className='px-3 py-6 text-center text-gray-500 dark:text-dark-text-secondary' colSpan={columns.length}>
                          {t('content.knowledgeGraph.importManager.validation.noData')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className='space-y-3 md:hidden'>
              {tableData.pageItems.map((m, idx) => {
                const fileName = m.external_path.split('/').pop() || '';
                const pathWithoutFile = m.external_path.replace('/' + fileName, '');
                const isConflict = m.mapping_status === 'mismatch';

                return (
                  <div
                    key={idx}
                    className={`rounded-lg border-2 bg-white p-4 dark:bg-dark-background-secondary ${
                      m.isPerfectMatch
                        ? 'border-green-300 bg-green-50 shadow-sm dark:border-green-600 dark:bg-green-900/20'
                        : m.mapping_status === 'found'
                          ? 'border-green-200 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                          : isConflict
                            ? 'border-orange-200 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                            : 'border-red-200 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                    }`}
                  >
                    {/* Header with file name and status */}
                    <div className='mb-2 flex items-start justify-between'>
                      <div className='min-w-0 flex-1'>
                        <div className='truncate font-semibold text-gray-900 dark:text-dark-text' title={fileName}>
                          {fileName}
                        </div>
                        <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary' title={pathWithoutFile}>
                          {pathWithoutFile}
                        </div>
                      </div>
                      <div className={`flex items-center rounded-full px-2 py-1 text-xs font-semibold ${m.statusColor} ml-2`}>
                        {m.statusIcon}
                        <span className='ml-1'>{m.mapping_status}</span>
                      </div>
                    </div>

                    {/* MD5 comparison */}
                    <div className='mb-2 space-y-1 text-xs'>
                      <div className='grid grid-cols-2 gap-2'>
                        <div className='flex flex-col'>
                          <div className='flex items-center text-gray-600 dark:text-dark-text-secondary'>
                            <HashtagIcon className='mr-1 h-3 w-3' />
                            <span className='font-medium'>External MD5:</span>
                          </div>
                          <span className='ml-4 mt-1 font-mono text-[11px] text-orange-600 dark:text-orange-400'>
                            {m.external_md5?.slice(0, 12)}...
                          </span>
                        </div>
                        <div className='flex flex-col'>
                          <div className='flex items-center text-gray-600 dark:text-dark-text-secondary'>
                            <HashtagIcon className='mr-1 h-3 w-3' />
                            <span className='font-medium'>Internal MD5:</span>
                          </div>
                          {m.internalMd5 ? (
                            <div className='ml-4 mt-1 flex items-center'>
                              <span
                                className={`font-mono text-[11px] ${
                                  m.external_md5?.toLowerCase() === m.internalMd5?.toLowerCase()
                                    ? 'font-medium text-green-600 dark:text-green-400'
                                    : 'font-medium text-red-600 dark:text-red-400'
                                }`}
                              >
                                {m.internalMd5.slice(0, 12)}...
                              </span>
                              {m.external_md5?.toLowerCase() === m.internalMd5?.toLowerCase() ? (
                                <CheckCircleIcon className='ml-1 h-3 w-3 text-green-500 dark:text-green-400' />
                              ) : (
                                <XMarkIcon className='ml-1 h-3 w-3 text-red-500 dark:text-red-400' />
                              )}
                            </div>
                          ) : (
                            <span className='ml-4 mt-1 text-[11px] italic text-gray-400 dark:text-dark-text-secondary'>
                              {m.mapping_status === 'missing' ? t('content.knowledgeGraph.importManager.table.notFound') : '-'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {tableData.pageItems.length === 0 && (
                <div className='py-8 text-center'>
                  <MagnifyingGlassIcon className='mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-dark-text-secondary' />
                  <div className='text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.knowledgeGraph.importManager.table.noDocumentsFound')}</div>
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className='flex flex-col items-start justify-between gap-2 text-sm text-gray-700 dark:text-dark-text-secondary sm:flex-row sm:items-center'>
              <div>
                {t('content.knowledgeGraph.importManager.validation.displaying', {
                  start: tableData.total === 0 ? 0 : tableData.start + 1,
                  end: Math.min(tableData.start + pageSize, tableData.total),
                  total: tableData.total
                })}
              </div>
              <div className='flex items-center gap-2'>
                <Button variant='outline' size='sm' onClick={() => setPage(Math.max(1, page - 1))} disabled={tableData.currentPage <= 1}>
                  {t('content.knowledgeGraph.importManager.validation.previous')}
                </Button>
                <span>
                  {t('content.knowledgeGraph.importManager.validation.page', {
                    current: tableData.currentPage,
                    total: tableData.totalPages
                  })}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage(Math.min(tableData.totalPages, page + 1))}
                  disabled={tableData.currentPage >= tableData.totalPages}
                >
                  {t('content.knowledgeGraph.importManager.validation.next')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KGValidationTable;
